import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { authClient, signIn } from "@/lib/auth-client";
import {
  DEV_ADMIN_PASSWORD,
  DEV_AUDITOR_PASSWORD,
  DEV_MANAGER_PASSWORD,
  DEV_STAKEHOLDER_PASSWORD,
} from "@/lib/dev-accounts";
import { devKeys } from "@/lib/query";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const isDev = process.env.NODE_ENV !== "production";

const seedAccountsSchema = z.object({
  admin: z.object({ email: z.string().email() }),
  manager: z.object({ email: z.string().email() }).optional(),
  auditor: z.object({ email: z.string().email() }).optional(),
  stakeholder: z.object({ email: z.string().email() }).optional(),
});

type DevAccount = { label: string; email: string; password: string };

// Emails come from a dev-only server endpoint so they always match the seeded
// rows, regardless of which host the studio opened the app at.
function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      const accounts: DevAccount[] = [
        { label: "Admin", email: parsed.admin.email, password: DEV_ADMIN_PASSWORD },
      ];
      if (parsed.manager) {
        accounts.push({
          label: "Manager",
          email: parsed.manager.email,
          password: DEV_MANAGER_PASSWORD,
        });
      }
      if (parsed.auditor) {
        accounts.push({
          label: "Auditor",
          email: parsed.auditor.email,
          password: DEV_AUDITOR_PASSWORD,
        });
      }
      if (parsed.stakeholder) {
        accounts.push({
          label: "Stakeholder",
          email: parsed.stakeholder.email,
          password: DEV_STAKEHOLDER_PASSWORD,
        });
      }
      return accounts;
    },
  });
}

export function LoginForm() {
  const router = useRouter();
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const devAccounts = useDevAccounts().data ?? [];
  const [ssoLoading, setSsoLoading] = React.useState(false);

  function fillCredentials(email: string, password: string) {
    form.setValue("email", email, { shouldValidate: true });
    form.setValue("password", password, { shouldValidate: true });
  }

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }

    router.navigate({ to: "/dashboard" });
  }

  async function onSignInWithMicrosoft() {
    setSsoLoading(true);
    const { error } = await authClient.signIn.social({
      provider: "microsoft",
      callbackURL: "/dashboard",
    });
    if (error) {
      toast.error(error.message ?? "Failed to sign in with Microsoft");
      setSsoLoading(false);
    }
  }

  return (
    <Card className="border-border/50 shadow-sm">
      <CardHeader className="space-y-1 text-center pb-2">
        <h1 className="font-display text-3xl font-bold">Sign In</h1>
        <p className="text-muted-foreground text-sm">Enter your credentials to continue</p>
      </CardHeader>
      <CardContent>
        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center gap-2"
          onClick={onSignInWithMicrosoft}
          disabled={ssoLoading}
        >
          <MicrosoftIcon />
          {ssoLoading ? "Redirecting…" : "Sign in with Microsoft"}
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs text-muted-foreground">
            <span className="bg-card px-2">or sign in with email</span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </Form>
        {isDev && devAccounts.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">Sign In As:</span>
            {devAccounts.map((account) => (
              <Button
                key={account.label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fillCredentials(account.email, account.password)}
              >
                {account.label}
              </Button>
            ))}
          </div>
        )}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Don't have an account?{" "}
          <a href="/signup" className="text-primary hover:underline underline-offset-4">
            Sign up
          </a>
        </p>
      </CardContent>
    </Card>
  );
}

function MicrosoftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 21 21"
      aria-hidden="true"
    >
      <title>Microsoft logo</title>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
